// Parent Progress tab: Movie Mode Quiz Insights — which movie lessons the
// child understood independently vs with Pip's help, plus concepts worth
// another review. Concise card with an expandable detail view.
import { useEffect, useState } from "react";
import { Clapperboard } from "lucide-react";
import { api } from "../../lib/api";

export const MovieInsightsCard = () => {
  const [insights, setInsights] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () =>
    api.get("/movie/family/quiz-insights")
      .then(({ data }) => setInsights(data.insights || []))
      .catch(() => setInsights([]));
  useEffect(() => { load(); }, []);
  const queueIt = (childId, item) =>
    api.post("/movie/family/review-queue", { child_id: childId, ...item })
      .then(load).catch(() => {});
  const removeIt = (queueId) =>
    api.delete(`/movie/family/review-queue/${queueId}`)
      .then(load).catch(() => {});
  if (!insights.length) return null;
  return (
    <section className="bg-card rounded-2xl border border-border p-5"
      data-testid="movie-insights-card">
      <h2 className="text-base md:text-lg font-bold font-display flex items-center gap-2 mb-1">
        <Clapperboard className="h-5 w-5 text-indigo-500" aria-hidden="true" />
        Movie Mode Quiz Insights
      </h2>
      <p className="text-xs text-muted-foreground mb-3">
        Quick understanding checks after each finished movie lesson.
      </p>
      <div className="space-y-3">
        {insights.map((r) => (
          <div key={r.child_id} className="rounded-xl border border-border p-3 space-y-2"
            data-testid={`movie-insights-${r.child_id}`}>
            <p className="text-sm font-bold">
              {r.child_name} — {r.total} quick check{r.total === 1 ? "" : "s"} ·{" "}
              <span className="text-emerald-700">{r.independent_count} independent</span> ·{" "}
              <span className="text-amber-700">{r.supported_count} with Pip's help</span>
            </p>
            <div className="flex flex-wrap gap-1.5">
              {r.aced.map((t) => (
                <span key={t} className="text-[11px] font-bold rounded-full bg-emerald-100 text-emerald-900 px-2.5 py-0.5">
                  Aced: {t}
                </span>
              ))}
              {r.review.map((item) => {
                const queued = r.queued.some((q) => q.topic_id === item.topic_id);
                return queued ? null : (
                  <button key={item.topic_id} type="button"
                    onClick={() => queueIt(r.child_id, item)}
                    className="text-[11px] font-bold rounded-full bg-amber-100 text-amber-900 px-2.5 py-0.5 border border-amber-300 hover:bg-amber-200"
                    title="Queue this lesson for your child's next session"
                    data-testid={`movie-insights-review-${r.child_id}`}>
                    Review again: {item.title} — Queue it
                  </button>
                );
              })}
            </div>
            {r.queued.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5" data-testid={`movie-insights-queued-${r.child_id}`}>
                <span className="text-[11px] font-bold text-muted-foreground">Queued for next session (recommended by you):</span>
                {r.queued.map((q) => (
                  <span key={q.queue_id} className="text-[11px] font-bold rounded-full bg-indigo-100 text-indigo-900 px-2.5 py-0.5 inline-flex items-center gap-1">
                    {q.title}
                    <button type="button" onClick={() => removeIt(q.queue_id)}
                      className="font-extrabold hover:text-indigo-600" aria-label={`Remove ${q.title} from queue`}
                      data-testid={`movie-insights-unqueue-${q.queue_id}`}>
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
            <button type="button"
              onClick={() => setOpen(open === r.child_id ? null : r.child_id)}
              className="text-xs font-bold text-primary underline underline-offset-2"
              data-testid={`movie-insights-toggle-${r.child_id}`}>
              {open === r.child_id ? "Hide details" : "View details"}
            </button>
            {open === r.child_id && (
              <ul className="space-y-1" data-testid={`movie-insights-detail-${r.child_id}`}>
                {r.recent.map((s, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex justify-between gap-2">
                    <span>
                      <span className="font-semibold text-foreground">{s.title}</span>{" "}
                      ({s.subject}) — {s.independent ? "understood independently" : "needed Pip's help"}
                    </span>
                    <span className="shrink-0">{(s.at || "").slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};
